import React, { useState, useEffect } from 'react';
import { Shield, Bell, Database, Users, Key, Building2, Landmark, Target, CheckCircle2, Circle, Mail, HardDrive, Inbox, Loader2, ChevronRight, Clock, MessageSquare, Palette, FileText, Trash2, Download } from 'lucide-react';
import api, { errorText } from '../config';
import { useAuth } from '../context/AuthContext';

export default function Settings() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'firm');
  const { user } = useAuth();
  const isAdmin = user?.role === 'Caply_Admin';
  const [integrations, setIntegrations] = useState(null);
  const [integError, setIntegError] = useState(null);
  const [integBusy, setIntegBusy] = useState(false);
  const [googleCreds, setGoogleCreds] = useState({});
  const [connectedNotice, setConnectedNotice] = useState(null);
  const [thesis, setThesis] = useState(null);
  const [thesisBusy, setThesisBusy] = useState(false);
  const [thesisSaved, setThesisSaved] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanLookback, setScanLookback] = useState(7);
  const [autoScanBusy, setAutoScanBusy] = useState(false);
  const [autoScanHours, setAutoScanHours] = useState(12);
  const [alertSettings, setAlertSettings] = useState(null);
  const [testSend, setTestSend] = useState(null); // {busy, sent, results, to_emails}
  const [testTo, setTestTo] = useState(''); // explicit test destination (blank = alert recipients)
  const [alertRecipients, setAlertRecipients] = useState('');
  const [alertBusy, setAlertBusy] = useState(false);
  const [notifications, setNotifications] = useState({ dealAlerts: true, reportReady: true, systemErrors: true, weeklyDigest: false });
  const [modelError, setModelError] = useState(null);
  const [team, setTeam] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', display_name: '', role: 'Analyst' });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { member, temp_password } | null
  const [inviteError, setInviteError] = useState(null);
  const [firmProfile, setFirmProfile] = useState(null);
  const [firmForm, setFirmForm] = useState(null); // draft edits
  const [lifecycle, setLifecycle] = useState(null);
  const [firmBusy, setFirmBusy] = useState(false);
  const [firmError, setFirmError] = useState(null);
  const [firmSaved, setFirmSaved] = useState(false);

  const loadFirm = () => {
    api.get('/api/firm/profile')
      .then(res => {
        setFirmProfile(res.data);
        setFirmForm({
          firm_name: res.data.firm_name || '',
          maturity: res.data.maturity || '',
          fund_name: res.data.fund_name || '',
          target_fund_size: res.data.target_fund_size ?? '',
          committed_capital: res.data.committed_capital ?? '',
          current_phase: res.data.current_phase || 'investing',
          milestones: res.data.milestones || [],
        });
      })
      .catch(err => setFirmError(errorText(err, 'Could not load firm profile.')));
    api.get('/api/firm/lifecycle')
      .then(res => setLifecycle(res.data))
      .catch(() => setLifecycle(null));
  };

  const saveFirm = async () => {
    setFirmBusy(true);
    setFirmError(null);
    setFirmSaved(false);
    try {
      const payload = {
        ...firmForm,
        target_fund_size: firmForm.target_fund_size === '' ? null : parseFloat(firmForm.target_fund_size),
        committed_capital: firmForm.committed_capital === '' ? null : parseFloat(firmForm.committed_capital),
      };
      const res = await api.put('/api/firm/profile', payload);
      setFirmProfile(res.data);
      setFirmForm({ ...firmForm, milestones: res.data.milestones || firmForm.milestones });
      setFirmSaved(true);
      const lc = await api.get('/api/firm/lifecycle').catch(() => null);
      if (lc) setLifecycle(lc.data);
    } catch (err) {
      setFirmError(errorText(err, 'Could not save firm profile.'));
    } finally {
      setFirmBusy(false);
    }
  };

  const toggleMilestone = (id) => {
    setFirmForm({
      ...firmForm,
      milestones: (firmForm.milestones || []).map(m => m.id === id ? { ...m, done: !m.done } : m),
    });
  };

  const tabs = [
    { id: 'firm', name: 'Firm & Fund', icon: Building2 },
    { id: 'team', name: 'Team & Access', icon: Users },
    { id: 'integrations', name: 'Integrations', icon: Database },
    { id: 'templates', name: 'Template Library', icon: Palette },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'security', name: 'Security', icon: Shield },
  ];


  const gmailInteg = (integrations?.integrations || []).find(i => i.provider === 'gmail');

  const saveAutoScan = async (enabled) => {
    setAutoScanBusy(true);
    try {
      const res = await api.put('/api/integrations/gmail/auto-scan', { enabled, hours: autoScanHours });
      setAutoScanHours(res.data.auto_scan_hours || autoScanHours);
      loadIntegrations();
    } catch (err) {
      setIntegError(errorText(err, 'Could not save auto-scan setting.'));
    } finally {
      setAutoScanBusy(false);
    }
  };

  const sendTestEmail = async () => {
    setTestSend({ busy: true });
    const to = testTo.split(',').map(x => x.trim()).filter(Boolean);
    try {
      const res = await api.post('/api/integrations/alerts/send-test', { to_emails: to });
      const refreshed = await api.get('/api/integrations/alerts/settings');
      setAlertSettings(refreshed.data);
      setTestSend({ busy: false, ...res.data });
    } catch (err) {
      setTestSend({ busy: false, sent: false, reason: err.response?.data?.detail || 'Could not send the test email.' });
    }
  };

  const saveAlertSettings = async (patch) => {
    setAlertBusy(true);
    try {
      const res = await api.put('/api/integrations/alerts/settings', patch);
      setAlertSettings(res.data);
      if (typeof res.data.recipients?.join === 'function') setAlertRecipients(res.data.recipients.join(', '));
    } catch (err) {
      setIntegError(errorText(err, 'Could not save alert email settings.'));
    } finally {
      setAlertBusy(false);
      setTestSend(null); // a settings change can invalidate the last test result
    }
  };

  const loadIntegrations = () => {
    api.get('/api/integrations')
      .then(res => setIntegrations(res.data))
      .catch(err => setIntegError(errorText(err, 'Could not load integrations.')));
    api.get('/api/integrations/thesis')
      .then(res => setThesis(res.data))
      .catch(() => setThesis({ thesis: '', sectors: [], geographies: [] }));
  };

  const saveCreds = (provider) => {
    const c = googleCreds[provider] || {};
    if (!c.client_id || !c.client_secret) { setIntegError('Enter both the Client ID and Client Secret.'); return; }
    setIntegBusy(true); setIntegError(null);
    api.put(`/api/integrations/${provider}/config`, { client_id: c.client_id, client_secret: c.client_secret })
      .then(() => loadIntegrations())
      .catch(err => setIntegError(errorText(err, 'Could not save credentials.')))
      .finally(() => setIntegBusy(false));
  };

  const connect = (provider, force = false) => {
    setIntegError(null);
    api.post(`/api/integrations/${provider}/connect${force ? '?force=1' : ''}`)
      .then(res => { window.open(res.data.consent_url, '_blank'); })
      .catch(err => setIntegError(errorText(err, 'Could not start the connection.')));
  };

  const disconnect = (provider) => {
    setIntegError(null);
    api.delete(`/api/integrations/${provider}`)
      .then(() => loadIntegrations())
      .catch(err => setIntegError(errorText(err, 'Could not disconnect.')));
  };

  const testConn = (provider) => {
    setIntegError(null); setIntegBusy(true);
    api.get(`/api/integrations/${provider}/test`)
      .then(res => { setConnectedNotice(`${provider === 'gmail' ? 'Gmail' : 'Google Drive'} connection verified (${res.data.email || 'account ok'}).`); loadIntegrations(); })
      .catch(err => setIntegError(errorText(err, 'Connection test failed.')))
      .finally(() => setIntegBusy(false));
  };

  const runScan = () => {
    setScanBusy(true); setScanResult(null); setIntegError(null);
    api.post(`/api/integrations/gmail/scan?lookback_days=${scanLookback}`)
      .then(res => setScanResult(res.data))
      .catch(err => setIntegError(errorText(err, 'Scan failed.')))
      .finally(() => { setScanBusy(false); loadIntegrations(); });
  };

  const saveThesis = () => {
    setThesisBusy(true); setThesisSaved(false);
    api.put('/api/integrations/thesis', {
      thesis: thesis.thesis || null,
      sectors: thesis.sectors,
      geographies: thesis.geographies,
      min_revenue_usd: thesis.min_revenue_usd ?? null,
      max_revenue_usd: thesis.max_revenue_usd ?? null,
      min_ebitda_margin_pct: thesis.min_ebitda_margin_pct ?? null,
      min_target_valuation_usd: thesis.min_target_valuation_usd ?? null,
      max_target_valuation_usd: thesis.max_target_valuation_usd ?? null,
    })
      .then(() => { setThesisSaved(true); setTimeout(() => setThesisSaved(false), 2500); })
      .catch(err => setIntegError(errorText(err, 'Could not save the thesis.')))
      .finally(() => setThesisBusy(false));
  };

  const openTplEditor = (kind) => {
    setTplError(null); setTplNotice(null);
    setTplEditor({ kind, name: '', file: null, fileName: '' });
  };

  const handleTplFile = (kind, file) => {
    if (!file) return;
    const ok = ['.pdf', '.pptx', '.docx'].some(ext => file.name.toLowerCase().endsWith(ext));
    if (!ok) {
      setTplError('Templates accept PDF, PowerPoint, and Word files only.');
      return;
    }
    setTplError(null);
    setTplEditor({ kind, name: file.name.replace(/\.[^.]+$/, ''), file, fileName: file.name });
  };

  const saveTemplate = async () => {
    if (!tplEditor.file) return;
    setTplBusy(true); setTplError(null); setTplNotice(null);
    try {
      const form = new FormData();
      form.append('kind', tplEditor.kind);
      form.append('name', tplEditor.name || tplEditor.fileName.replace(/\.[^.]+$/, ''));
      form.append('file', tplEditor.file);
      await api.post('/api/dd/templates/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      loadTemplates();
      setTplEditor({ kind: null, name: '', file: null, fileName: '' });
      setTplNotice('Template saved — new IOI / LOI / IC memo / board deck PDFs will use it.');
    } catch (err) {
      setTplError(errorText(err, 'Could not save the template.'));
    } finally { setTplBusy(false); }
  };

  const removeTemplate = async (kind) => {
    setTplBusy(true); setTplError(null); setTplNotice(null);
    try {
      await api.delete(`/api/dd/templates/${kind}`);
      loadTemplates();
      setTplNotice('Removed — documents go back to the Caply default format.');
    } catch (err) {
      setTplError(errorText(err, 'Could not remove the template.'));
    } finally { setTplBusy(false); }
  };

  const loadTeam = () => {
    setTeamLoading(true);
    setTeamError(null);
    api.get('/api/team')
      .then(res => setTeam(res.data))
      .catch(err => setTeamError(errorText(err, 'Could not load team members.')))
      .finally(() => setTeamLoading(false));
  };


  // Deep links (#card-id) from the onboarding checklist land directly on the
  // right section: wait for the tab's async content, then scroll + highlight.
  useEffect(() => {
    const scrollToHash = () => {
      const anchor = window.location.hash.replace('#', '');
      if (!anchor) return;
      let tries = 0;
      const timer = setInterval(() => {
        const el = document.getElementById(anchor);
        if (el) {
          clearInterval(timer);
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          el.classList.add('ring-2', 'ring-amber-400', 'rounded-xl');
          setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 2600);
        } else if (++tries > 12) clearInterval(timer);
      }, 250);
    };
    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, []);

  const [templates, setTemplates] = useState(null);
  const [tplEditor, setTplEditor] = useState({ kind: null, name: '', file: null, fileName: '' });
  const [tplBusy, setTplBusy] = useState(false);
  const [tplNotice, setTplNotice] = useState(null);
  const [tplError, setTplError] = useState(null);
  const [placeholders, setPlaceholders] = useState(null);
  const [branding, setBranding] = useState({ configured: false, caption: '', logo_filename: null });
  const [brandForm, setBrandForm] = useState({ caption: '', file: null, fileName: '' });
  const [brandBusy, setBrandBusy] = useState(false);
  const [brandNotice, setBrandNotice] = useState(null);

  const loadTemplates = () => {
    api.get('/api/dd/templates')
      .then(res => {
        setTemplates(res.data.templates || []);
        setPlaceholders(res.data.placeholders || null);
        const b = res.data.branding || { configured: false, caption: '', logo_filename: null };
        setBranding(b);
        setBrandForm(f => ({ ...f, caption: b.caption || '' }));
      })
      .catch(() => setTemplates([]));
  };

  const saveBranding = async () => {
    setBrandBusy(true); setBrandNotice(null);
    try {
      const form = new FormData();
      form.append('caption', brandForm.caption || '');
      if (brandForm.file) form.append('file', brandForm.file);
      await api.post('/api/dd/templates/branding', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBrandNotice('Branding saved — it now fills your templates automatically.');
      setBrandForm(f => ({ ...f, file: null, fileName: '' }));
      loadTemplates();
    } catch (e) {
      setBrandNotice(errorText(e, 'Could not save branding.'));
    } finally { setBrandBusy(false); }
  };

  const clearBranding = async () => {
    setBrandBusy(true); setBrandNotice(null);
    try {
      await api.delete('/api/dd/templates/branding');
      setBrandNotice('Branding removed.');
      setBrandForm({ caption: '', file: null, fileName: '' });
      loadTemplates();
    } catch (e) {
      setBrandNotice(errorText(e, 'Could not remove branding.'));
    } finally { setBrandBusy(false); }
  };

  useEffect(() => {
    if (tab === 'team' && !team && !teamLoading) {
      loadTeam();
    }
    if (tab === 'firm' && !firmProfile) {
      loadFirm();
    }
    if (tab === 'integrations' && !integrations) {
      loadIntegrations();
    }
    if (tab === 'templates' && !templates) {
      loadTemplates();
    }
    if (tab === 'integrations' && !alertSettings) {
      api.get('/api/integrations/alerts/settings')
        .then(res => { setAlertSettings(res.data); setAlertRecipients((res.data.recipients || []).join(', ')); })
        .catch(() => {});
    }
  }, [tab]); // eslint-disable-line

  // Arriving back from the Google OAuth callback (?connected=gmail|gdrive)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    if (connected) {
      setTab('integrations');
      setConnectedNotice(`${connected === 'gmail' ? 'Gmail' : 'Google Drive'} connected successfully.`);
      window.history.replaceState({}, '', '/settings');
      setTimeout(() => setConnectedNotice(null), 6000);
    }
  }, []);


  return (
    <div className="page-container max-w-4xl mx-auto">
      <div>
        <h2 className="page-title">Settings</h2>
        <p className="page-subtitle">Your firm, your team, and your connections — all in one place</p>
      </div>

      <div className="flex gap-6">
        {/* Tab sidebar */}
        <div className="w-48 shrink-0 space-y-1">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                  tab === t.id ? 'bg-brand-50 text-brand-700 border-l-2 border-brand-500' : 'text-slate-500 hover:bg-slate-50 border-l-2 border-transparent'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.name}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="flex-1">
          {tab === 'templates' && (
            <div className="card space-y-5">
              <div className="card-header">
                <h3 className="card-title">
                  <Palette className="w-5 h-5 text-brand-600" />
                  Template Library
                </h3>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Upload your firm's branded, lawyer-approved templates for each document.
                Caply generates every IOI, LOI, Investment Memo, and Board Deck as a
                formatted PDF through your template — written only from the deal's real
                data, in your firm's look. Accepted formats: PDF, PowerPoint, and Word.
                Each row has a <b>Sample</b> you can download, edit and re-upload.
              </p>

              {/* ── Firm branding (logo + caption) used inside your templates ── */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-navy-900">Your firm branding</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Upload your logo and a short company caption once — Caply drops them into your templates
                    wherever the header placeholders are.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="form-label">Firm logo (PNG, JPG or SVG)</label>
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg"
                      className="text-xs text-slate-500"
                      disabled={!isAdmin}
                      onChange={e => {
                        const f = e.target.files[0];
                        setBrandForm(b => ({ ...b, file: f || null, fileName: f ? f.name : '' }));
                      }}
                    />
                    {brandForm.fileName
                      ? <p className="text-xs text-emerald-600 mt-1">Selected: {brandForm.fileName}</p>
                      : branding.logo_filename && (
                        <p className="text-xs text-slate-500 mt-1">Current logo: {branding.logo_filename}</p>
                      )}
                  </div>
                  <div>
                    <label className="form-label">Company caption (one line)</label>
                    <input
                      className="form-input"
                      value={brandForm.caption}
                      disabled={!isAdmin}
                      onChange={e => setBrandForm(b => ({ ...b, caption: e.target.value }))}
                      placeholder="e.g. Growth equity in logistics & industrial technology"
                    />
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-2 items-center">
                    <button onClick={saveBranding} disabled={brandBusy} className="btn-primary text-xs py-1.5 px-3 disabled:opacity-50">
                      {brandBusy ? 'Saving…' : 'Save branding'}
                    </button>
                    {branding.configured && (
                      <button onClick={clearBranding} disabled={brandBusy}
                        className="text-xs font-medium text-rose-600 hover:text-rose-700 px-2.5 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 transition disabled:opacity-50">
                        Remove branding
                      </button>
                    )}
                    {brandNotice && <span className="text-xs text-slate-600">{brandNotice}</span>}
                  </div>
                )}
              </div>

              {/* ── Placeholder legend ── */}
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 space-y-1">
                <p className="font-medium text-slate-700">Placeholders your template can use</p>
                <p>
                  <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded">{'<<content>>'}</code>{' '}
                  — where Caply writes the generated document at run time. Leave it exactly as it is.
                </p>
                <p>
                  <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded">{'{{firm_logo}}'}</code>{' '}
                  <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded">{'{{firm_name}}'}</code>{' '}
                  <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded">{'{{company_caption}}'}</code>{' '}
                  — filled from the branding saved above.
                </p>
                <p>
                  <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded">{'{{deal_name}}'}</code>{' '}
                  <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded">{'{'}{'{date}}'}</code>{' '}
                  — filled with the deal's name and today's date.
                </p>
                <p className="text-slate-400">
                  Caply keeps the rest of your template untouched — including the footer attribution line.
                </p>
              </div>
              {!isAdmin && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Only firm admins can change templates. Ask your admin to upload the firm's approved letterhead.
                </p>
              )}
              {templates === null ? (
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
              ) : (
                <div className="space-y-3">
                  {templates.map(t => (
                    <div key={t.kind} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-navy-900">{t.label} — {t.doc_title}</p>
                          {t.is_default ? (
                            <p className="text-xs text-slate-500 mt-0.5">Using the Caply default format</p>
                          ) : (
                            <p className="text-xs text-emerald-600 mt-0.5">
                              Branded template: {t.template_name} ({t.filename})
                              {t.created_by ? ` · by ${t.created_by}` : ''}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 items-center">
                          {t.sample_url && (
                            <a href={t.sample_url} download={t.sample_name}
                              className="btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1.5">
                              <Download className="w-3.5 h-3.5" />Sample
                            </a>
                          )}
                          {tplEditor.kind !== t.kind && (
                            <button onClick={() => openTplEditor(t.kind)} className="btn-secondary text-xs py-1.5 px-3">
                              {t.is_default ? 'Upload template' : 'Replace'}
                            </button>
                          )}
                          {!t.is_default && tplEditor.kind !== t.kind && (
                            <button onClick={() => removeTemplate(t.kind)} disabled={tplBusy}
                              className="text-xs font-medium text-rose-600 hover:text-rose-700 px-2.5 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 transition disabled:opacity-50">
                              Remove
                            </button>
                          )}
                        </div>
                      </div>

                      {tplEditor.kind === t.kind && (
                        <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <FileText className="w-3.5 h-3.5" />
                            Upload a PDF, PowerPoint (.pptx), or Word (.docx) file. Not sure where to start?
                            Download the <b>Sample</b> above — it is a working Word template with the
                            placeholders already in place. For Word templates, put{' '}
                            <code className="bg-slate-100 px-1.5 py-0.5 rounded">{'<<content>>'}</code> where the
                            document body should go; your header, footer and styles are kept as-is.
                          </div>
                          <div>
                            <label className="form-label">Template file</label>
                            <input
                              type="file"
                              accept=".pdf,.pptx,.docx"
                              className="text-xs text-slate-500"
                              onChange={e => handleTplFile(t.kind, e.target.files[0])}
                            />
                            {tplEditor.fileName && (
                              <p className="text-xs text-emerald-600 mt-1">Selected: {tplEditor.fileName}</p>
                            )}
                          </div>
                          <div>
                            <label className="form-label">Template name</label>
                            <input
                              className="form-input"
                              value={tplEditor.name}
                              onChange={e => setTplEditor(f => ({ ...f, name: e.target.value }))}
                              placeholder="e.g. Firm letterhead v2"
                            />
                          </div>
                          {tplError && <p className="text-xs text-rose-600">{tplError}</p>}
                          <div className="flex gap-2">
                            <button onClick={saveTemplate} disabled={tplBusy || !tplEditor.file} className="btn-primary text-xs py-1.5 px-3 disabled:opacity-50">
                              {tplBusy ? 'Saving…' : 'Save template'}
                            </button>
                            <button onClick={() => setTplEditor({ kind: null, name: '', file: null, fileName: '' })} className="btn-secondary text-xs py-1.5 px-3">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {tplNotice && (
                <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{tplNotice}</p>
              )}
            </div>
          )}

          {tab === 'security' && (
            <div className="card space-y-5">
              <div className="card-header">
                <h3 className="card-title">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  Your Data &amp; Security
                </h3>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Secure sign-in with Microsoft', desc: 'Each teammate signs in with their own work account — no shared passwords' },
                  { label: 'Your firm’s data stays private', desc: 'Your data is isolated to your firm — no other firm can ever see it' },
                  { label: 'Encrypted storage for keys & credentials', desc: 'Connection keys and secrets are stored encrypted, never in plain text' },
                  { label: 'Full activity history', desc: 'Every important change is recorded, so you always know who did what' },
                ].map(s => (
                  <div key={s.label} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                    <div>
                      <p className="text-sm font-medium text-navy-900">{s.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
                    </div>
                    <span className="badge badge-green">
                      <span className="status-dot status-dot-green mr-1" />
                      Active
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'notifications' && (
            <div className="card space-y-5">
              <div className="card-header">
                <h3 className="card-title">
                  <Bell className="w-5 h-5 text-brand-600" />
                  Notification Preferences
                </h3>
              </div>
              <div className="space-y-3">
                {Object.entries(notifications).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                    <span className="text-sm font-medium text-navy-900">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase())}
                    </span>
                    <button
                      onClick={() => setNotifications({ ...notifications, [key]: !val })}
                      className={`relative w-11 h-6 rounded-full transition ${val ? 'bg-brand-600' : 'bg-slate-300'}`}
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition ${val ? 'left-6' : 'left-1'}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'integrations' && (
            <div className="space-y-6">
              {connectedNotice && (
                <div className="card p-4 border-green-300 bg-green-50">
                  <p className="text-sm text-green-800">{connectedNotice}</p>
                </div>
              )}
              {integError && (
                <div className="card p-4 border-red-300 bg-red-50">
                  <p className="text-sm text-red-700">{integError}</p>
                </div>
              )}

              {/* Google Workspace */}
              <div id="google-workspace" className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    <HardDrive className="w-5 h-5 text-brand-600" />
                    Google Workspace
                  </h3>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Connect the firm's Gmail and Google Drive to auto-import deal materials (CIM, teaser, IM) and
                  organize them under <span className="font-mono text-slate-700">Caply/Deals/&lt;Company&gt;</span> in Drive.
                  Caply uses read-only Gmail access and only manages folders it creates.
                </p>

                {(integrations?.integrations || []).some(i => !i.one_click_available) && (
                <details className="mt-3 group rounded-lg border border-slate-200 bg-slate-50/60">
                  <summary className="cursor-pointer select-none px-4 py-2.5 text-xs font-semibold text-slate-700 flex items-center gap-2">
                    <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                    New here? Step-by-step Google OAuth setup guide
                  </summary>
                  <div className="px-4 pb-4 pt-1 space-y-3 text-xs text-slate-600 leading-relaxed">
                    <ol className="list-decimal list-outside space-y-2 pl-4 marker:font-semibold marker:text-slate-400">
                      <li>
                        In <a className="text-brand-600 underline" href="https://console.cloud.google.com/" target="_blank" rel="noreferrer">Google Cloud Console</a>,
                        create (or pick) a project, then enable the <b>Gmail API</b> and <b>Google Drive API</b>
                        {' '}(<span className="font-mono">APIs &amp; Services → Library</span>).
                      </li>
                      <li>
                        Configure the <b>OAuth consent screen</b> (External, app name "Caply") and add the Gmail
                        addresses of the people who will connect Caply as <b>test users</b>.
                      </li>
                      <li>
                        Create <b>Credentials → OAuth client ID</b> of type <b>Web application</b>. Under Authorized
                        redirect URIs, paste the two redirect URIs shown on the Gmail and Drive cards below
                        (they end in <span className="font-mono">/gmail/callback</span> and{' '}
                        <span className="font-mono">/gdrive/callback</span>).
                      </li>
                      <li>
                        Google shows a <b>Client ID</b> and <b>Client Secret</b>. Paste them into the Gmail card below,
                        <b> Save credentials</b>, then click <b>Connect</b> and approve the Google consent screen
                        (the "unverified app" warning is normal while your app is in Testing mode). Repeat for the
                        Google Drive card — the same credentials work.
                      </li>
                      <li>
                        Click <b>Test connection</b> on each card, then run your first <b>Deal Inbox Scan</b> below.
                      </li>
                    </ol>
                    <p className="text-slate-500">
                      Stuck? The full guide with troubleshooting (redirect_uri_mismatch, invalid_client, and more)
                      lives in the <b>User Guide</b> under Settings → Google OAuth setup.
                    </p>
                  </div>
                </details>
                )}

                <div className="space-y-4 mt-4">
                  {(integrations?.integrations || []).map(integ => {
                    const creds = googleCreds[integ.provider] || {};
                    return (
                      <div key={integ.provider} className="p-4 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {integ.provider === 'gmail'
                              ? <Mail className="w-5 h-5 text-slate-600" />
                              : <HardDrive className="w-5 h-5 text-slate-600" />}
                            <div>
                              <p className="text-sm font-semibold text-navy-900">{integ.label}</p>
                              <p className="text-xs text-slate-500">
                                {integ.connected
                                  ? `Connected as ${integ.connected_email || 'firm account'}`
                                  : integ.configured
                                    ? 'Credentials saved — not connected yet'
                                    : 'Not configured'}
                              </p>
                            </div>
                          </div>
                          <span className={`badge shrink-0 ${integ.connected ? 'badge-green' : integ.configured ? 'badge-amber' : 'badge-slate'}`}>
                            <span className={`status-dot mr-1 ${integ.connected ? 'status-dot-green' : 'status-dot-slate'}`} />
                            {integ.connected ? 'Connected' : integ.configured ? 'Ready' : 'Not set up'}
                          </span>
                        </div>

                        {isAdmin && !integ.connected && (
                          <div className="mt-4 space-y-3">
                            {integ.one_click_available && !integ.configured ? (
                              <div>
                                <button className="btn-primary" onClick={() => connect(integ.provider)}>
                                  Connect {integ.label}
                                </button>
                                <p className="text-xs text-slate-400 mt-2">
                                  One click: sign in with your firm's Google account and approve. No API keys or tokens needed.
                                </p>
                              </div>
                            ) : integ.configured ? (
                              <button className="btn-primary" onClick={() => connect(integ.provider)}>
                                Connect {integ.label}
                              </button>
                            ) : (
                              <p className="text-xs text-slate-500">
                                Save your firm's Google credentials under Advanced below, then connect.
                              </p>
                            )}

                            <p className="text-xs text-slate-400 leading-relaxed">
                              Heads-up: Google may show an "unverified app" screen during sign-in. That's normal —
                              click <strong>Advanced → Go to Caply</strong> to continue. Your data stays in your firm's accounts.
                            </p>

                            {!integ.connected && (
                              <details className="group rounded-lg border border-slate-200 bg-slate-50/60">
                                <summary className="cursor-pointer select-none px-4 py-2.5 text-xs font-semibold text-slate-600 flex items-center gap-2">
                                  <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                                  Advanced — use your firm's own Google Cloud app
                                </summary>
                                <div className="px-4 pb-4 pt-1 space-y-3">
                                  <p className="text-xs text-slate-500">
                                    Most firms don't need this. Use it only if your IT policy requires your own Google Cloud project.
                                  </p>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="form-label">Google OAuth Client ID</label>
                                      <input
                                        type="text"
                                        className="form-input font-mono text-xs"
                                        placeholder="1234567890-xxx.apps.googleusercontent.com"
                                        value={creds.client_id || ''}
                                        onChange={e => setGoogleCreds({ ...googleCreds, [integ.provider]: { ...creds, client_id: e.target.value } })}
                                      />
                                    </div>
                                    <div>
                                      <label className="form-label">Client Secret</label>
                                      <input
                                        type="password"
                                        className="form-input font-mono text-xs"
                                        placeholder="GOCSPX-..."
                                        value={creds.client_secret || ''}
                                        onChange={e => setGoogleCreds({ ...googleCreds, [integ.provider]: { ...creds, client_secret: e.target.value } })}
                                      />
                                    </div>
                                  </div>
                                  <div className="text-xs text-slate-400">
                                    Add this redirect URI in Google Cloud Console → OAuth client:
                                    <code className="ml-1 font-mono text-slate-600 break-all">{integrations?.redirect_uris?.[integ.provider]}</code>
                                  </div>
                                  {!integ.configured && (
                                    <button className="btn-secondary" disabled={integBusy} onClick={() => saveCreds(integ.provider)}>
                                      Save credentials
                                    </button>
                                  )}
                                </div>
                              </details>
                            )}
                          </div>
                        )}

                        {integ.connected && integ.managed_by === 'platform' && (
                          <p className="text-xs text-slate-400 mt-2">Connected via Caply's managed Google app.</p>
                        )}

                        {integ.connected && (
                          <div className="mt-4 flex gap-2">
                            <button className="btn-secondary" onClick={() => testConn(integ.provider)}>Test connection</button>
                            {isAdmin && <button className="btn-secondary" onClick={() => connect(integ.provider, true)}>Reconnect</button>}
                            {isAdmin && <button className="btn-secondary text-red-600" onClick={() => disconnect(integ.provider)}>Disconnect</button>}
                          </div>
                        )}
                        {integ.last_scan_summary && integ.provider === 'gmail' && (
                          <p className="text-xs text-slate-400 mt-3">
                            Last scan: {integ.last_scan_summary.deals_created} deal(s) created from {integ.last_scan_summary.scanned} attachment email(s)
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {!integrations && !integError && <p className="text-sm text-slate-400">Loading…</p>}
                </div>

                {(integrations?.roadmap || []).length > 0 && (
                  <div className="mt-6">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">On the roadmap</p>
                    <div className="grid gap-3 md:grid-cols-3">
                      {(integrations.roadmap || []).map(r => (
                        <div key={r.provider} className="p-4 rounded-lg border border-dashed border-slate-200 bg-slate-50/40">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <MessageSquare className="w-4 h-4 text-slate-400" />
                              <p className="text-sm font-semibold text-slate-700">{r.label}</p>
                            </div>
                            <span className="badge badge-slate shrink-0">
                              <Clock className="w-3 h-3 mr-1" />Planned
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-2 leading-relaxed">{r.summary}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Gmail deal-material scan */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    <Inbox className="w-5 h-5 text-brand-600" />
                    Deal Inbox Scan (CIM Screening)
                  </h3>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Scans the connected Gmail inbox for new-deal materials (CIM, teaser, IM, management presentation),
                  creates a screened deal per new company with a memo built strictly from the email and document contents,
                  and files attachments into Drive. Nothing is invented — undisclosed metrics are reported as not disclosed.
                </p>
                <div className="flex items-center gap-3 mt-4">
                  <select className="form-select w-40" value={scanLookback} onChange={e => setScanLookback(Number(e.target.value))}>
                    <option value={3}>Last 3 days</option>
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                  </select>
                  <button
                    className="btn-primary flex items-center gap-2"
                    disabled={scanBusy || !(integrations?.integrations || []).find(i => i.provider === 'gmail')?.connected}
                    onClick={runScan}
                  >
                    {scanBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Inbox className="w-4 h-4" />}
                    {scanBusy ? 'Scanning…' : 'Scan Gmail now'}
                  </button>
                  {!(integrations?.integrations || []).find(i => i.provider === 'gmail')?.connected && (
                    <span className="text-xs text-slate-400">Connect Gmail above to enable scanning.</span>
                  )}
                </div>

                {gmailInteg?.connected && (
                  <div className="mt-4 p-3 rounded-lg border border-slate-200 bg-slate-50/60">
                    <label className="flex items-center gap-2 text-sm text-navy-900 font-medium">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={!!gmailInteg.auto_scan_enabled}
                        disabled={autoScanBusy || !isAdmin}
                        onChange={e => saveAutoScan(e.target.checked)}
                      />
                      Automatically scan the inbox every{' '}
                      <select
                        className="form-select w-20 py-1"
                        value={gmailInteg.auto_scan_hours || autoScanHours}
                        disabled={autoScanBusy || !isAdmin}
                        onChange={e => { setAutoScanHours(Number(e.target.value)); if (gmailInteg.auto_scan_enabled) saveAutoScan(true); }}
                      >
                        <option value={6}>6 h</option>
                        <option value={12}>12 h</option>
                        <option value={24}>24 h</option>
                      </select>
                      {autoScanBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
                    </label>
                    <p className="text-xs text-slate-500 mt-1.5">
                      New deal emails land on the New Deal pipeline automatically — each is screened and filed in Drive without anyone clicking Scan.
                      {!isAdmin && ' (Firm admins only.)'}
                    </p>
                  </div>
                )}

                {scanResult && (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm text-slate-600">
                      Scanned {scanResult.scanned} attachment email(s) · {scanResult.deal_materials_found} with deal materials ·
                      {' '}{scanResult.deals_created?.length || 0} new deal(s) created
                      {scanResult.drive_organized ? ' · attachments organized in Drive' : ''}
                    </p>
                    {(scanResult.deals_created || []).map(d => (
                      <div key={d.deal_id} className="p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                        <p className="text-sm font-medium text-navy-900">{d.company_name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {d.sector} · screening {d.memo_status === 'SUCCESS' ? 'complete' : 'partial'} · {d.files_organized} file(s) in Drive · visible on the New Deal pipeline
                        </p>
                      </div>
                    ))}
                    {scanResult.message && <p className="text-xs text-slate-400">{scanResult.message}</p>}
                  </div>
                )}
              </div>

              {/* Monitoring alert email digests */}
              <div id="alert-emails" className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    <Bell className="w-5 h-5 text-brand-600" />
                    Alert Emails
                  </h3>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  When Caply's daily portfolio check finds new decline alerts (margin below stage-aware threshold,
                  sequential revenue or EBITDA declines), it emails a digest of the new alerts. Sent via your connected
                  Gmail. Emails are sent for NEW alerts only — existing active alerts are never re-mailed.
                </p>
                {alertSettings && (
                  <div className="mt-4 space-y-3">
                    <label className="flex items-center gap-2 text-sm text-navy-900 font-medium">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={!!alertSettings.email_enabled}
                        disabled={alertBusy || !isAdmin}
                        onChange={e => saveAlertSettings({ email_enabled: e.target.checked, recipients: alertRecipients.split(',').map(s => s.trim()).filter(Boolean), sender_name: alertSettings.sender_name || '', sender_email: alertSettings.sender_email || '' })}
                      />
                      Email me new alerts
                      {alertBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
                    </label>
                    <div>
                      <label className="form-label">Send from — display name (optional)</label>
                      <input
                        className="form-input"
                        placeholder="e.g. Sarah Chen"
                        value={alertSettings.sender_name || ''}
                        disabled={alertBusy || !isAdmin}
                        onChange={e => setAlertSettings({ ...alertSettings, sender_name: e.target.value })}
                      />
                      <label className="form-label mt-3">Send from — email address (optional)</label>
                      <input
                        className="form-input"
                        placeholder="deals@yourfirm.com"
                        value={alertSettings.sender_email || ''}
                        disabled={alertBusy || !isAdmin}
                        onChange={e => setAlertSettings({ ...alertSettings, sender_email: e.target.value })}
                      />
                      <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                        Applies to every email Caply sends (alert digests and approved outreach). The address must be
                        a verified <a className="underline text-slate-500" href="https://support.google.com/mail/answer/22370" target="_blank" rel="noreferrer">"Send mail as" alias</a> of
                        the connected Gmail account — otherwise Gmail sends from the connected account as before.
                      </p>
                      <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-xs font-medium text-slate-700">
                            {alertSettings.sender_verified_for && alertSettings.sender_verified_for === alertSettings.sender_email ? (
                              <span className="text-emerald-700">✓ Sending verified — test email delivered to {alertSettings.sender_verified_for}</span>
                            ) : (
                              'Not verified yet'
                            )}
                          </span>
                          <div className="flex items-center gap-2">
                            <input
                              className="form-input text-xs py-1.5 px-3 w-64"
                              placeholder="Test destination (blank = alert recipients)"
                              value={testTo}
                              disabled={testSend?.busy || alertBusy || !isAdmin}
                              onChange={e => setTestTo(e.target.value)}
                            />
                            <button
                              className="btn-secondary text-xs py-1.5 px-3"
                              onClick={sendTestEmail}
                              disabled={testSend?.busy || alertBusy || !isAdmin}
                            >
                              {testSend?.busy ? 'Sending…' : 'Send test email'}
                            </button>
                          </div>
                        </div>
                        {testSend && !testSend.busy && (
                          <div className={`text-xs mt-2 leading-relaxed ${testSend.sent && testSend.sender_honored !== false ? 'text-emerald-700' : 'text-rose-600'}`}>
                            {testSend.sent ? (
                              <>
                                Sent to {(testSend.to_emails || []).join(', ')}.
                                {testSend.sender_honored === false ? (
                                  <> But Gmail replaced your sender with the connected account (<em>{testSend.from_used}</em>).
                                  {' '}{(testSend.results || []).find(r => r.hint)?.hint}</>
                                ) : (
                                  <> Check that inbox (and spam) — if it arrived, your sender settings work.</>
                                )}
                              </>
                            ) : (
                              <>
                                Could not send to {(testSend.to_emails || []).join(', ')}:{' '}
                                {(testSend.results || []).filter(r => !r.sent).map(r => r.reason || 'unknown error').join('; ')}
                                {(testSend.results || []).some(r => !r.sent && r.hint) ? ' ' + testSend.results.find(r => r.hint)?.hint : ''}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="form-label">Recipients (comma-separated)</label>
                      <input
                        className="form-input"
                        placeholder="partner@firm.com, cfo@firm.com"
                        value={alertRecipients}
                        disabled={alertBusy || !isAdmin}
                        onChange={e => setAlertRecipients(e.target.value)}
                      />
                      {isAdmin && alertRecipients && (
                        <button className="btn-secondary mt-2 text-xs" onClick={() => saveAlertSettings({ email_enabled: !!alertSettings.email_enabled, recipients: alertRecipients.split(',').map(s => s.trim()).filter(Boolean), sender_name: alertSettings.sender_name || '', sender_email: alertSettings.sender_email || '' })}>
                          Save recipients
                        </button>
                      )}
                      {!isAdmin && <p className="text-xs text-slate-400 mt-1">Firm admins only.</p>}
                    </div>
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-3">
                  Note: sending requires the Gmail "send" permission. Firms that connected Gmail before this feature
                  may need to Disconnect and re-connect Gmail once.
                </p>
              </div>

              {/* Investment thesis */}
              <div id="investment-thesis" className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    <Target className="w-5 h-5 text-brand-600" />
                    Investment Thesis (Screening Criteria)
                  </h3>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  The criteria Caply screens every deal against — form submissions and Gmail imports alike.
                  Matches are evaluated honestly: criteria without data are marked insufficient, never guessed.
                </p>
                {thesis && (
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className="form-label">Thesis statement</label>
                      <textarea
                        className="form-input"
                        rows={3}
                        placeholder="e.g. B2B software and tech-enabled services in North America, $20-100M revenue, 20%+ EBITDA margins…"
                        value={thesis.thesis || ''}
                        onChange={e => setThesis({ ...thesis, thesis: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="form-label">Target sectors (comma-separated)</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="HealthTech, B2B Software, Logistics"
                          value={Array.isArray(thesis.sectors) ? thesis.sectors.join(', ') : ''}
                          onChange={e => setThesis({ ...thesis, sectors: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                        />
                      </div>
                      <div>
                        <label className="form-label">Geographies (comma-separated)</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="North America, Europe"
                          value={Array.isArray(thesis.geographies) ? thesis.geographies.join(', ') : ''}
                          onChange={e => setThesis({ ...thesis, geographies: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className="form-label">Min revenue ($)</label>
                        <input type="number" className="form-input" value={thesis.min_revenue_usd ?? ''} onChange={e => setThesis({ ...thesis, min_revenue_usd: e.target.value === '' ? null : Number(e.target.value) })} />
                      </div>
                      <div>
                        <label className="form-label">Max revenue ($)</label>
                        <input type="number" className="form-input" value={thesis.max_revenue_usd ?? ''} onChange={e => setThesis({ ...thesis, max_revenue_usd: e.target.value === '' ? null : Number(e.target.value) })} />
                      </div>
                      <div>
                        <label className="form-label">Min EBITDA margin (%)</label>
                        <input type="number" className="form-input" value={thesis.min_ebitda_margin_pct ?? ''} onChange={e => setThesis({ ...thesis, min_ebitda_margin_pct: e.target.value === '' ? null : Number(e.target.value) })} />
                      </div>
                      <div>
                        <label className="form-label">Max valuation ($)</label>
                        <input type="number" className="form-input" value={thesis.max_target_valuation_usd ?? ''} onChange={e => setThesis({ ...thesis, max_target_valuation_usd: e.target.value === '' ? null : Number(e.target.value) })} />
                      </div>
                    </div>
                    <div className="flex justify-end items-center gap-3">
                      {thesisSaved && <span className="text-xs text-green-600">Saved</span>}
                      <button className="btn-primary" disabled={thesisBusy} onClick={saveThesis}>
                        {thesisBusy ? 'Saving…' : 'Save thesis'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Where your data lives */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    <Database className="w-5 h-5 text-brand-600" />
                    Where Your Data Lives
                  </h3>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Caply builds every report and dashboard from data your firm provides — nothing else.
                </p>
                <div className="space-y-2.5 mt-3">
                  {[
                    { name: 'Your documents & files', desc: 'Stored encrypted, private to your firm' },
                    { name: 'Your deals, companies & reports', desc: 'Everything you enter, kept separate per firm' },
                    { name: 'Your connections (Gmail, Drive)', desc: 'Access keys stored encrypted, revocable anytime' },
                  ].map(i => (
                    <div key={i.name} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                      <div className="pr-4">
                        <p className="text-sm font-medium text-navy-900">{i.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{i.desc}</p>
                      </div>
                      <span className="badge badge-green shrink-0">
                        <span className="status-dot status-dot-green mr-1" />
                        Active
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {tab === 'team' && (
            <div className="card space-y-5">
              <div className="card-header">
                <h3 className="card-title">
                  <Users className="w-5 h-5 text-brand-600" />
                  Team & Access
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Members of your firm only. Invitations create a real Caply sign-in account
                  with a one-time password.
                </p>
              </div>

              {teamLoading && <p className="text-sm text-slate-500">Loading team…</p>}
              {teamError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{teamError}</div>
              )}

              {team && (
                <div className="space-y-3">
                  {team.members.length === 0 && (
                    <p className="text-sm text-slate-500">No members yet — invite your first teammate below.</p>
                  )}
                  {team.members.map(m => (
                    <div key={m.member_id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 font-bold flex items-center justify-center text-sm">
                          {(m.display_name || m.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-navy-900">{m.display_name}</p>
                          <p className="text-xs text-slate-500">{m.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {m.status === 'invited' && (
                          <span className="badge badge-amber">
                            <span className="status-dot status-dot-amber mr-1" />
                            Invited
                          </span>
                        )}
                        <select
                          value={m.role}
                          onChange={(e) => {
                            const role = e.target.value;
                            api.patch(`/api/team/members/${m.member_id}`, { role })
                              .then(() => loadTeam())
                              .catch(err => setTeamError(errorText(err, 'Could not change the role.')));
                          }}
                          className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1 text-slate-700"
                          title="Permission level"
                        >
                          <option value="Admin">Admin</option>
                          <option value="Analyst">Analyst</option>
                        </select>
                        <button
                          className="text-xs text-red-600 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50"
                          onClick={() => {
                            if (!window.confirm(`Remove ${m.display_name} (${m.email}) from your firm? Their Caply access will be disabled.`)) return;
                            api.delete(`/api/team/members/${m.member_id}`)
                              .then(() => loadTeam())
                              .catch(err => setTeamError(errorText(err, 'Could not remove member.')));
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {inviteResult && (
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 space-y-2">
                  <p className="text-sm font-semibold text-emerald-800">
                    {inviteResult.email_sent
                      ? 'Invitation emailed'
                      : inviteResult.temp_password
                        ? 'Invitation created — share these credentials'
                        : 'Invitation sent'}
                  </p>
                  {inviteResult.email_note && (
                    <p className="text-xs text-emerald-700">{inviteResult.email_note}</p>
                  )}
                  {inviteResult.temp_password ? (
                    <>
                      <p className="text-xs text-emerald-700">
                        {inviteResult.member.display_name} ({inviteResult.member.email}) can now sign in at the Caply
                        sign-in page with the temporary password below. They will be asked to change it at first
                        sign-in. <strong>This password is shown only once.</strong>
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-3 py-2 rounded bg-white border border-emerald-200 text-sm font-mono text-navy-900 select-all">
                          {inviteResult.temp_password}
                        </code>
                        <button
                          className="btn-secondary"
                          onClick={() => navigator.clipboard.writeText(inviteResult.temp_password)}
                        >
                          <Key className="w-4 h-4" />
                          Copy
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-emerald-700">
                      {inviteResult.member.display_name} ({inviteResult.member.email}) already has a Caply account —
                      they can sign in with their existing password and will now see your firm's data.
                    </p>
                  )}
                  <button
                    className="text-xs text-emerald-700 underline hover:text-emerald-800"
                    onClick={() => { setInviteResult(null); setInviteOpen(false); }}
                  >
                    Done
                  </button>
                </div>
              )}

              {inviteOpen && !inviteResult && (
                <div className="p-4 rounded-lg border border-slate-200 bg-slate-50/50 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                      <input
                        className="form-input w-full"
                        type="email"
                        placeholder="analyst@firm.com"
                        value={inviteForm.email}
                        onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Full name</label>
                      <input
                        className="form-input w-full"
                        placeholder="Jane Smith"
                        value={inviteForm.display_name}
                        onChange={e => setInviteForm({ ...inviteForm, display_name: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Role</label>
                    <select
                      className="form-input w-48"
                      value={inviteForm.role}
                      onChange={e => setInviteForm({ ...inviteForm, role: e.target.value })}
                    >
                      <option value="Analyst">Analyst</option>
                      <option value="Admin">Admin</option>
                    </select>
                  </div>
                  {inviteError && (
                    <p className="text-xs text-red-600">{inviteError}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      className="btn-primary"
                      disabled={inviteBusy}
                      onClick={() => {
                        setInviteBusy(true);
                        setInviteError(null);
                        api.post('/api/team/invite', inviteForm)
                          .then(res => {
                            setInviteResult(res.data);
                            loadTeam();
                          })
                          .catch(err => setInviteError(errorText(err, 'Invitation failed.')))
                          .finally(() => setInviteBusy(false));
                      }}
                    >
                      {inviteBusy ? 'Creating account…' : 'Send invitation'}
                    </button>
                    <button className="btn-secondary" onClick={() => { setInviteOpen(false); setInviteError(null); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {!inviteOpen && (
                <button
                  className="btn-secondary w-full"
                  onClick={() => { setInviteOpen(true); setInviteResult(null); }}
                >
                  <Key className="w-4 h-4" />
                  Invite Team Member
                </button>
              )}
            </div>
          )}

          {tab === 'firm' && firmForm && (
            <div id="firm-profile" className="card space-y-6">
              <div className="card-header">
                <h3 className="card-title">
                  <Building2 className="w-5 h-5 text-brand-600" />
                  Firm Profile & Fund Lifecycle
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Tell Caply where your firm is in its journey — this tunes the platform to your stage
                  (admins only). Nothing here is shared outside your firm workspace.
                </p>
              </div>

              {firmError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{firmError}</div>
              )}
              {firmSaved && !firmError && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">
                  Firm profile saved.
                </div>
              )}

              {/* Firm basics */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Firm name</label>
                  <input
                    className="form-input"
                    placeholder="e.g. Alpha Capital Partners"
                    value={firmForm.firm_name}
                    onChange={e => setFirmForm({ ...firmForm, firm_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">Current fund name</label>
                  <input
                    className="form-input"
                    placeholder="e.g. Fund II LP"
                    value={firmForm.fund_name}
                    onChange={e => setFirmForm({ ...firmForm, fund_name: e.target.value })}
                  />
                </div>
              </div>

              {/* Maturity picker */}
              <div>
                <label className="form-label">Firm maturity</label>
                <div className="grid grid-cols-3 gap-3">
                  {(firmProfile?.maturity_options || []).map(m => (
                    <button
                      key={m.key}
                      onClick={() => setFirmForm({ ...firmForm, maturity: m.key })}
                      className={`p-3 rounded-lg border text-left transition ${
                        firmForm.maturity === m.key
                          ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-300'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <p className="text-sm font-semibold text-navy-900">{m.label}</p>
                      <p className="text-xs text-slate-500 mt-1 leading-snug">{m.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Fund size + phase */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Target fund size ($)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="100000000"
                    value={firmForm.target_fund_size}
                    onChange={e => setFirmForm({ ...firmForm, target_fund_size: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">Committed capital ($)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="75000000"
                    value={firmForm.committed_capital}
                    onChange={e => setFirmForm({ ...firmForm, committed_capital: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">Current phase</label>
                  <p className="text-xs text-slate-400 mb-1 -mt-1">Switching phases refreshes the milestone list on save.</p>
                  <select
                    className="form-select"
                    value={firmForm.current_phase}
                    onChange={e => setFirmForm({ ...firmForm, current_phase: e.target.value })}
                  >
                    {(firmProfile?.phase_options || []).map(p => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Phase description + milestones */}
              {(() => {
                const phaseInfo = (firmProfile?.phase_options || []).find(p => p.key === firmForm.current_phase);
                if (!phaseInfo) return null;
                return (
                  <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 space-y-3">
                    <p className="text-xs text-slate-600">
                      <span className="font-semibold text-navy-900">{phaseInfo.label}:</span> {phaseInfo.description}
                    </p>
                    {(firmForm.milestones || []).length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-slate-600">Milestones for this phase</p>
                        {firmForm.milestones.map(m => (
                          <button
                            key={m.id}
                            onClick={() => toggleMilestone(m.id)}
                            className="flex items-center gap-2 text-sm w-full text-left px-2 py-1 rounded hover:bg-white transition"
                          >
                            {m.done
                              ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                              : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
                            <span className={m.done ? 'text-slate-400 line-through' : 'text-navy-900'}>{m.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Computed lifecycle stats (real portfolio data) */}
              {lifecycle && (
                <div className="p-4 rounded-lg bg-navy-50 border border-navy-100 space-y-3">
                  <p className="text-xs font-semibold text-navy-900 flex items-center gap-1.5">
                    <Landmark className="w-4 h-4" />
                    Fund status (computed from your live portfolio)
                  </p>
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-slate-500">Deployed</p>
                      <p className="font-mono font-semibold text-navy-900">
                        {lifecycle.deployed_capital >= 1e6
                          ? `$${(lifecycle.deployed_capital / 1e6).toFixed(1)}M`
                          : `$${(lifecycle.deployed_capital / 1e3).toFixed(0)}K`}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Active holdings</p>
                      <p className="font-mono font-semibold text-navy-900">{lifecycle.active_holdings}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Exits</p>
                      <p className="font-mono font-semibold text-navy-900">{lifecycle.exited_holdings}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Fund deployed</p>
                      <p className="font-mono font-semibold text-navy-900">
                        {lifecycle.deployment_pct != null ? `${lifecycle.deployment_pct}%` : '—'}
                      </p>
                    </div>
                  </div>
                  {lifecycle.deployment_pct != null && (
                    <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className="h-full bg-brand-600 rounded-full transition-all"
                        style={{ width: `${Math.min(lifecycle.deployment_pct, 100)}%` }}
                      />
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(lifecycle.stage_mix || {}).map(([k, v]) => (
                      <span key={k} className="badge badge-slate text-xs">
                        {k.replace('_', '-')} : {v}
                      </span>
                    ))}
                    {lifecycle.unstagged_holdings > 0 && (
                      <span className="badge badge-amber text-xs">{lifecycle.unstagged_holdings} untagged</span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button className="btn-primary" onClick={saveFirm} disabled={firmBusy}>
                  {firmBusy ? 'Saving…' : 'Save Firm Profile'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
